/**
 * La scheda «Profili e regimi»: i profili di compenso (quanto per ogni mese di abbonamento) e i regimi fiscali (IVA,
 * ritenuta, cassa). Si creano e si spengono; non si modificano né si cancellano, perché li citano abbinamenti e
 * liquidazioni.
 */

import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import type { RegimeFiscaleDoc } from '@shared/partner/tipi';
import { spegniProfilo, spegniRegime, type ConId, type DatiProgrammaPartner } from '../../services/partnerConsoleService';
import { DialogoProfilo } from './DialogoProfilo';
import { DialogoRegime } from './DialogoRegime';
import type { Esegui } from './esegui';
import { useFormati } from './formati';
import { ScalaCompenso } from './ScalaCompenso';

interface Props {
  dati: DatiProgrammaPartner;
  esegui: Esegui;
  onErrore: (messaggio: string) => void;
  onRicarica: () => void;
}

export const SchedaProfili: React.FC<Props> = ({ dati, esegui, onErrore, onRicarica }) => {
  const { t } = useTranslation('partner');
  const f = useFormati();
  const [nuovoProfilo, setNuovoProfilo] = React.useState(false);
  const [nuovoRegime, setNuovoRegime] = React.useState(false);

  const descriviRegime = (r: ConId<RegimeFiscaleDoc>) => [
    r.iva_percentuale ? t('profili.iva', { percentuale: f.percentuale(r.iva_percentuale) }) : t('profili.senza_iva'),
    r.ritenuta
      ? t('profili.ritenuta', { aliquota: f.percentuale(r.ritenuta.aliquota), base: f.percentuale(r.ritenuta.base_percentuale) })
      : t('profili.senza_ritenuta'),
    r.cassa
      ? t('profili.cassa', { nome: r.cassa.nome, nostra: f.percentuale(r.cassa.quota_nostra), sua: f.percentuale(r.cassa.quota_partner) })
      : t('profili.senza_cassa'),
  ];

  const sezione = (
    titolo: string,
    aiuto: string,
    bottone: string,
    onNuovo: () => void,
    vuoto: string,
    voci: Array<{ id: string; nome: string; attivo: boolean; contenuto: React.ReactNode; onSpegni: () => void }>,
  ) => (
    <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: tokens.color.graphite }}>{titolo}</Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, maxWidth: 620 }}>{aiuto}</Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={onNuovo} sx={{ textTransform: 'none' }}>{bottone}</Button>
        </Box>
      </Stack>
      {voci.length === 0 && <Typography variant="body2" sx={{ color: tokens.color.mist }}>{vuoto}</Typography>}
      <Stack spacing={1}>
        {voci.map((v) => (
          <Stack
            key={v.id}
            direction="row"
            alignItems="flex-start"
            spacing={2}
            sx={{ p: 1.5, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.lineSoft}`, opacity: v.attivo ? 1 : 0.6 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.nome}</Typography>
                {!v.attivo && <SoftChip label={t('profili.spento')} />}
              </Stack>
              <Box sx={{ mt: 1 }}>{v.contenuto}</Box>
            </Box>
            {v.attivo && (
              <Button size="small" onClick={v.onSpegni} sx={{ textTransform: 'none', flexShrink: 0 }}>{t('azioni.spegni')}</Button>
            )}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2}>
      {sezione(
        t('profili.titolo_profili'), t('profili.aiuto_profili'), t('azioni.nuovo_profilo'), () => setNuovoProfilo(true), t('vuoti.profili'),
        dati.profili.map((p) => ({ id: p.id, nome: p.nome, attivo: p.attivo, contenuto: <Box sx={{ maxWidth: 520 }}><ScalaCompenso scaglioni={p.scaglioni} /></Box>, onSpegni: () => void esegui(() => spegniProfilo(p.id)) })),
      )}
      {sezione(
        t('profili.titolo_regimi'), t('profili.aiuto_regimi'), t('azioni.nuovo_regime'), () => setNuovoRegime(true), t('vuoti.regimi'),
        dati.regimi.map((r) => ({
          id: r.id, nome: r.nome, attivo: r.attivo, onSpegni: () => void esegui(() => spegniRegime(r.id)),
          contenuto: descriviRegime(r).map((riga) => <Typography key={riga} variant="caption" sx={{ color: tokens.color.ash, display: 'block' }}>{riga}</Typography>),
        })),
      )}
      <DialogoProfilo aperto={nuovoProfilo} onChiudi={() => setNuovoProfilo(false)} onSalvato={onRicarica} onErrore={onErrore} />
      <DialogoRegime aperto={nuovoRegime} onChiudi={() => setNuovoRegime(false)} onSalvato={onRicarica} onErrore={onErrore} />
    </Stack>
  );
};
